import React, { useEffect, useRef } from "react";
import { Flex, Box } from "@chakra-ui/react";
import { useInstantSearch, useCurrentRefinements, useClearRefinements } from "react-instantsearch";
import { useHeaderHeight } from "contexts/header-height";
import { AlgoliaSearchInput } from "components/algolia";
import { Section, sectionSpacingY } from "components/section";
import { Chip } from "ui/chips";
import PlaybooksSearchResults from "./playbooks-search-results";
import PlaybooksSidebarFilters from "./playbooks-sidebar-filters";

function PlaybooksCatalog() {
  const catalogRef = useRef<HTMLDivElement>(null);
  const { results } = useInstantSearch();
  const { refine: clearFilters } = useClearRefinements();
  const { items: currentExperienceFilter } = useCurrentRefinements({ includedAttributes: ["experienceLevel"] });
  const { items: currentIndustryFilter } = useCurrentRefinements({ includedAttributes: ["industry.name"] });
  const { items: currentTeamFilter } = useCurrentRefinements({ includedAttributes: ["team.name"] });
  const { refine: clearExperience } = useClearRefinements({ includedAttributes: ["experienceLevel"] });
  const { refine: clearIndustry } = useClearRefinements({ includedAttributes: ["industry.name"] });
  const { refine: clearTeam } = useClearRefinements({ includedAttributes: ["team.name"] });

  const currentExperience = currentExperienceFilter?.[0]?.refinements?.[0];
  const currentIndustry = currentIndustryFilter?.[0]?.refinements?.[0];
  const currentTeam = currentTeamFilter?.[0]?.refinements?.[0];

  const { headerHeight } = useHeaderHeight();

  useEffect(() => {
    // Reset filters to "all" when beginning a search
    if (results?.query.length === 1) {
      clearFilters();
    }
  }, [results?.query.length, clearFilters]);

  useEffect(() => {
    // Scroll catalog to top when search begins or filter clicked
    if (
      catalogRef?.current &&
      window.scrollY !== catalogRef.current.offsetTop &&
      results?.query.length < 2 &&
      window.scrollY > 80 // Don't scroll on page load
    ) {
      window.scrollTo({ top: catalogRef.current.offsetTop - headerHeight, behavior: "smooth" });
    }
  }, [currentExperience, currentIndustry, currentTeam, results?.query.length, headerHeight]);

  return (
    <Section my={0} pb={sectionSpacingY} minH={results?.nbHits > 2 ? `calc(100vh - ${headerHeight}px)` : "auto"}>
      <Flex
        ref={catalogRef}
        direction={{ base: "column", lg: "row" }}
        align="start"
        justify="space-between"
        h="100%"
        minH={0}
      >
        <PlaybooksSidebarFilters
          query={results?.query ?? ""}
          headerHeight={`${headerHeight}px`}
          sidebarTop={[6, 6, 12]}
        />

        <Box w={["100%", "100%", "100%", "75%"]}>
          {/* only sticky if page is scrollable */}
          <Box
            position={results?.nbHits > 2 ? "sticky" : "relative"}
            w="100%"
            top={results?.nbHits > 2 ? `${headerHeight}px` : 0}
            zIndex={300}
          >
            {/* negative x-margins to hide card shadows on scroll */}
            <Box bg="white" w={`calc(100% + ${16})`} pt={[6, 6, 12]} mx={{ base: -4, xl: -8 }} px={{ base: 4, xl: 8 }}>
              <Box w="100%">
                <AlgoliaSearchInput placeholder="Search for a destination or use case..." />

                <Flex
                  display={{ base: "none", lg: "flex" }}
                  gap={4}
                  pt={currentIndustry || currentExperience || currentTeam ? 6 : 0}
                >
                  {currentIndustry && (
                    <Chip
                      variant="gray"
                      onCloseClick={() => {
                        clearIndustry();
                      }}
                    >
                      {currentIndustry.label}
                    </Chip>
                  )}

                  {currentExperience && (
                    <Chip
                      variant="gray"
                      onCloseClick={() => {
                        clearExperience();
                      }}
                    >
                      <Box as="span" textTransform="capitalize">
                        {currentExperience.label}
                      </Box>
                    </Chip>
                  )}

                  {currentTeam && (
                    <Chip
                      variant="gray"
                      onCloseClick={() => {
                        clearTeam();
                      }}
                    >
                      {currentTeam.label}
                    </Chip>
                  )}
                </Flex>
              </Box>
            </Box>

            <Box
              mx={{ base: 0, xl: -8 }}
              w={`calc(100% + ${16})`}
              h={8}
              bgGradient="linear(#fff, 90%, rgba(255, 255, 255, 0))"
            />
          </Box>

          <PlaybooksSearchResults noFilters={!currentIndustry && !currentExperience && !currentTeam} />
        </Box>
      </Flex>
    </Section>
  );
}

export default PlaybooksCatalog;
